import React, { useRef, useState } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { generateAdmissionNo, getCourseDuration } from "./utils/letterUtils";

export default function Acknowledgement({ student: vendor }) {
  const printRef = useRef();

  const [formData, setFormData] = useState({
    refNo: generateAdmissionNo({
      prefix: "ACK",
      course: vendor?.selectedCourse || vendor?.serviceScope,
      studentId: vendor?.studentId || vendor?.vendorId,
    }),
    date: new Date().toLocaleDateString("en-GB"),
    studentName:
      [vendor?.firstName, vendor?.middleName, vendor?.lastName]
        .filter(Boolean)
        .join(" ") ||
      vendor?.vendorName ||
      vendor?.name ||
      "",
    parentName: vendor?.fatherName || vendor?.contactPerson || "Authorized Representative",
    course: vendor?.selectedCourse || vendor?.serviceScope || "Enterprise Logistics & Supply Chain",
    academicYear: vendor?.academicYear || "FY 2026-27",
    duration: getCourseDuration(
      vendor?.selectedCourse || vendor?.serviceScope || "",
      vendor?.academicYear || ""
    ) || "12 Months",
    admissionNo: vendor?.studentId || vendor?.vendorId || "VND-88019",

    university: "Global ERP Enterprise Network",
    recognition: "ISO 9001:2026 Certified & Corporate Regulatory Standards",

    instituteName: "GLOBAL LOGISTICS & MANUFACTURING CORP",
    approvalLine: "(Corporate Procurement & Supply Chain Division)",
    address: "Bangalore, Karnataka",
  });

  const [documents, setDocuments] = useState([
    "Certificate of Incorporation",
    "GST Registration Certificate",
    "PAN / Tax Identification Copy",
    "Non-Disclosure Agreement (Signed NDA)",
    "Vendor Identification & Director Verification Form",
    "Bank Account Verification & Cancelled Cheque",
    "Quality Compliance & ISO Certification",
  ]);

  const [selectedDocs, setSelectedDocs] = useState([...documents]);
  const [newDoc, setNewDoc] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDocChange = (doc) => {
    if (selectedDocs.includes(doc)) {
      setSelectedDocs(selectedDocs.filter((d) => d !== doc));
    } else {
      setSelectedDocs([...selectedDocs, doc]);
    }
  };

  const handleAddDoc = () => {
    if (newDoc.trim() && !documents.includes(newDoc)) {
      setDocuments([...documents, newDoc]);
      setSelectedDocs([...selectedDocs, newDoc]);
      setNewDoc("");
    }
  };

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "", "width=900,height=700");
    win.document.write(`
      <html>
        <head>
          <title>Document Receipt Acknowledgement</title>
          <style>
            body { font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; padding: 120px 20px 20px; color: #222; }
            .print-container { line-height: 1.8; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <Container className="mt-4">
      {/* FORM */}
      <Form className="mb-4">
        <Row className="mb-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Ref / Transaction ID</Form.Label>
              <Form.Control name="refNo" value={formData.refNo} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Acknowledgement Date</Form.Label>
              <Form.Control name="date" value={formData.date} onChange={handleChange} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Vendor / Enterprise Name</Form.Label>
              <Form.Control name="studentName" value={formData.studentName} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Authorized Representative</Form.Label>
              <Form.Control name="parentName" value={formData.parentName} onChange={handleChange} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Project Scope / Services</Form.Label>
              <Form.Control name="course" value={formData.course} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Fiscal / Contract Period</Form.Label>
              <Form.Control name="academicYear" value={formData.academicYear} onChange={handleChange} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Contract Term Duration</Form.Label>
              <Form.Control name="duration" value={formData.duration} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Vendor ID / Account Ref</Form.Label>
              <Form.Control name="admissionNo" value={formData.admissionNo} onChange={handleChange} />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {/* DOCUMENT SELECTION */}
      <h5 className="mb-3">Received Compliance Documents Checklist</h5>

      {documents.map((doc, idx) => (
        <Form.Check
          key={idx}
          type="checkbox"
          label={doc}
          checked={selectedDocs.includes(doc)}
          onChange={() => handleDocChange(doc)}
          className="mb-1"
        />
      ))}

      {/* ADD DOCUMENT */}
      <Row className="mt-3">
        <Col md={8}>
          <Form.Control
            placeholder="Add new compliance document..."
            value={newDoc}
            onChange={(e) => setNewDoc(e.target.value)}
          />
        </Col>
        <Col xs="auto">
          <Button variant="outline-primary" onClick={handleAddDoc}>Add Item</Button>
        </Col>
      </Row>

      {/* SELECT ALL / CLEAR */}
      <Row className="mt-2">
        <Col xs="auto">
          <Button size="sm" variant="secondary" onClick={() => setSelectedDocs(documents)}>
            Select All
          </Button>
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="outline-secondary" onClick={() => setSelectedDocs([])}>
            Clear All
          </Button>
        </Col>
      </Row>

      {/* PRINT BUTTON */}
      <div className="text-end mt-4">
        <Button onClick={handlePrint} variant="primary">Print Acknowledgement</Button>
      </div>

      {/* LETTER */}
      <div
        ref={printRef}
        style={{ padding: 30, border: "1px solid #ccc", marginTop: "20px" }}
        className="print-container bg-white"
      >
        {/* Header */}
        <Row>
          <Col>
            Ref: <strong>{formData.refNo}</strong>
          </Col>
          <Col className="text-end">
            Date: <strong>{formData.date}</strong>
          </Col>
        </Row>

        {/* Title */}
        <Row className="mt-4">
          <Col className="text-center">
            <h2 style={{ marginTop: "30px" }}>
              <u>RECEIPT & COMPLIANCE ACKNOWLEDGEMENT</u>
            </h2>
          </Col>
        </Row>

        {/* Body */}
        <Row className="mt-4">
          <Col>
            <p style={{ marginTop: "20px" }}>
              This document serves as formal confirmation that partner organisation{" "}
              <strong>{formData.studentName}</strong>, represented by{" "}
              <strong>{formData.parentName}</strong>, has been onboarding into our Enterprise Partner Framework for the scope of{" "}
              <strong>{formData.course}</strong> across contract period{" "}
              <strong>{formData.academicYear}</strong>.
            </p>

            <p>
              The designated operational term is <strong>{formData.duration}</strong>. The associated Vendor Identification Reference ID is <strong>{formData.admissionNo}</strong>.
            </p>

            <p style={{ marginTop: "30px" }}>
              We formally acknowledge the receipt and verification of the following corporate, compliance, and regulatory documents:
            </p>

            <ol style={{ paddingLeft: "60px" }}>
              {selectedDocs.map((doc, idx) => (
                <li key={idx} style={{ marginBottom: "8px" }}>
                  {doc}
                </li>
              ))}
            </ol>
          </Col>
        </Row>

        {/* Footer */}
        <Row className="mt-5" style={{ paddingTop: "50px" }}>
          <Col className="text-end">
            <p>
              <strong>AUTHORIZED PROCUREMENT CONTROLLER</strong>
            </p>
            <p style={{ fontSize: "12px", color: "#666" }}>
              Global Operations & Procurement Services
            </p>
          </Col>
        </Row>
      </div>
    </Container>
  );
}